import IconArrowLeft16 from '@warp-ds/icons/react/arrow-left-16';
import IconArrowRight16 from '@warp-ds/icons/react/arrow-right-16';
import IconDoubleBed16 from '@warp-ds/icons/react/double-bed-16';
import IconHouse32 from '@warp-ds/icons/react/house-32';
import IconStairs16 from '@warp-ds/icons/react/stairs-16';
import { Affix, Button, Select, TextField } from '@warp-ds/react';
import { type FC, type FormEvent, type ReactNode, useState } from 'react';
import type { DeviceType } from '../agent';
import { useOnVisible } from '../hooks/useOnVisible';
import { CLIENT_ID, ESTIMATE_URL, REFERRAL_SOURCE_PARAM, withReferralSource } from '../pristips/constants';
import { PANEL_SHELL_CLASSNAME } from '../pristips/dimensions';
import { fetchSelectedAdIds } from '../pristips/fetchSelectedAdIds';
import type { Address } from '../pristips/inputForm';
import { createInputFormDataFromFormData, type FormErrors, PropertyType } from '../pristips/inputForm';
import { createParamsFromInputFormData, toQueryString } from '../pristips/predictParams';
import { isValidAddress, validateInputForm } from '../pristips/validation';
import { trackClickPristipsFormNext, trackClickPristipsFormSubmit, trackViewPristipsForm } from '../utils/tracking';
import { AddressSearch } from './pristips/AddressSearch';
import { IconAffix } from './pristips/IconAffix';

interface Props {
    deviceType: DeviceType;
    selectedAdIdsUrl: string;
}

const Field: FC<{ children: ReactNode }> = ({ children }) => <div className="flex-1 min-w-0">{children}</div>;

export const PristipsFormPanel: FC<Props> = ({ deviceType, selectedAdIdsUrl }) => {
    const sectionRef = useOnVisible<HTMLElement>(trackViewPristipsForm);
    const [step, setStep] = useState(1);
    const [address, setAddress] = useState<Address>();
    const [propertyType, setPropertyType] = useState<PropertyType | ''>('');
    const [size, setSize] = useState('');
    const [bedrooms, setBedrooms] = useState('');
    const [floor, setFloor] = useState('');
    const [errors, setErrors] = useState<FormErrors>();
    const [isSubmitting, setIsSubmitting] = useState(false);

    const clearError = (key: keyof FormErrors) => setErrors((current) => ({ ...current, [key]: undefined }));

    const goToStep2 = () => {
        if (!isValidAddress(address)) {
            setErrors({ ...errors, address: 'Adresse er påkrevd' });
            return;
        }
        trackClickPristipsFormNext();
        setErrors(undefined);
        setStep(2);
    };

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const inputFormData = createInputFormDataFromFormData(new FormData(event.currentTarget));
        const formErrors = validateInputForm(inputFormData);
        if (formErrors) {
            setErrors(formErrors);
            return;
        }

        // Opened before the await so the browser still counts it as user-initiated and lets it through.
        const estimateWindow = deviceType === 'desktop' ? window.open('', '_blank') : null;

        trackClickPristipsFormSubmit();
        setIsSubmitting(true);

        const params = createParamsFromInputFormData(inputFormData);
        const selectedAdIds = await fetchSelectedAdIds(selectedAdIdsUrl, params);
        const url = withReferralSource(
            `${ESTIMATE_URL}?${toQueryString({ ...params, selectedAdIds, initialPrediction: true })}`,
        );

        setIsSubmitting(false);

        if (estimateWindow) estimateWindow.location.href = url;
        else window.top?.location.assign(url);
    };

    return (
        <section className={`${PANEL_SHELL_CLASSNAME} flex min-h-[386px]`} ref={sectionRef}>
            <form className="flex flex-col w-full p-24" action={ESTIMATE_URL} method="get" onSubmit={handleSubmit}>
                <input type="hidden" name="initialPrediction" value="true" />
                <input type="hidden" name={REFERRAL_SOURCE_PARAM} value={CLIENT_ID} />
                <input type="hidden" name="address.lat" value={address?.lat ?? ''} />
                <input type="hidden" name="address.lon" value={address?.lon ?? ''} />
                <input type="hidden" name="address.name" value={address?.name ?? ''} />
                <input type="hidden" name="address.label" value={address?.label ?? ''} />
                <input type="hidden" name="address.municipalityName" value={address?.municipalityName ?? ''} />
                <input type="hidden" name="address.postalCode" value={address?.postalCode ?? ''} />

                <div className="flex flex-col items-center text-center">
                    <span className="s-text-link mb-8" aria-hidden="true">
                        <IconHouse32 />
                    </span>
                    <h3>Hva er boligen din verdt?</h3>
                    <p className="s-text-subtle">Få et raskt estimat basert på salg i ditt område med Pristips.</p>
                </div>

                <div className="mt-8 md:mt-24">
                    {step === 1 ? (
                        <AddressSearch
                            address={address}
                            setAddress={setAddress}
                            error={errors?.address}
                            clearError={() => clearError('address')}
                        />
                    ) : (
                        <div className="flex flex-col md:flex-row gap-8">
                            <Field>
                                <Select
                                    label="Boligtype"
                                    name="propertyType"
                                    className={`w-full ${propertyType ? '' : 'is-placeholder'}`}
                                    value={propertyType}
                                    invalid={!!errors?.propertyType}
                                    hint={errors?.propertyType ?? null}
                                    onChange={(event) => {
                                        setPropertyType(event.target.value as PropertyType);
                                        clearError('propertyType');
                                    }}
                                >
                                    <option value="" disabled>
                                        Boligtype
                                    </option>
                                    <option value={PropertyType.FLAT}>Leilighet</option>
                                    <option value={PropertyType.DETACHED}>Enebolig</option>
                                    <option value={PropertyType.TERRACED_SEMIDETACHED}>Rekkehus / Tomannsbolig</option>
                                </Select>
                            </Field>
                            {/* display:contents lifts these into the grid on desktop, so every field gets its own
                                equal column. Important, because Warp's own .flex rule is inlined after Uno's. */}
                            <div className="flex flex-row gap-16 md:contents">
                                <Field>
                                    <TextField
                                        label="Størrelse"
                                        type="number"
                                        inputMode="numeric"
                                        pattern="[0-9]*"
                                        name="size"
                                        min="1"
                                        className="w-full"
                                        invalid={!!errors?.size}
                                        helpText={errors?.size ?? null}
                                        value={size}
                                        onChange={(event) => {
                                            setSize(event.target.value);
                                            clearError('size');
                                        }}
                                    >
                                        <Affix suffix label="m&sup2;" />
                                    </TextField>
                                </Field>
                                <Field>
                                    <TextField
                                        label="Soverom"
                                        type="number"
                                        inputMode="numeric"
                                        pattern="[0-9]*"
                                        name="bedrooms"
                                        className="w-full"
                                        invalid={!!errors?.bedrooms}
                                        helpText={errors?.bedrooms ?? null}
                                        value={bedrooms}
                                        onChange={(event) => {
                                            setBedrooms(event.target.value);
                                            clearError('bedrooms');
                                        }}
                                    >
                                        <IconAffix suffix>
                                            <IconDoubleBed16 />
                                        </IconAffix>
                                    </TextField>
                                </Field>
                                {propertyType === PropertyType.FLAT && (
                                    <Field>
                                        <TextField
                                            label="Etasje"
                                            type="number"
                                            inputMode="numeric"
                                            pattern="[0-9]*"
                                            name="floor"
                                            className="w-full"
                                            invalid={!!errors?.floor}
                                            helpText={errors?.floor ?? null}
                                            value={floor}
                                            onChange={(event) => {
                                                setFloor(event.target.value);
                                                clearError('floor');
                                            }}
                                        >
                                            <IconAffix suffix>
                                                <IconStairs16 />
                                            </IconAffix>
                                        </TextField>
                                    </Field>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                <div className="mt-auto pt-24 flex gap-16">
                    {step === 1 ? (
                        <Field>
                            <Button primary fullWidth type="button" className="w-full max-w-none" onClick={goToStep2}>
                                <span className="flex items-center justify-center gap-8">
                                    Neste
                                    <IconArrowRight16 />
                                </span>
                            </Button>
                        </Field>
                    ) : (
                        <>
                            <Field>
                                <Button type="button" fullWidth onClick={() => setStep(1)}>
                                    <span className="flex items-center justify-center gap-8 s-text-link">
                                        <IconArrowLeft16 />
                                        Tilbake
                                    </span>
                                </Button>
                            </Field>
                            <Field>
                                <Button primary fullWidth type="submit" loading={isSubmitting}>
                                    Se prisestimat
                                </Button>
                            </Field>
                        </>
                    )}
                </div>
            </form>
        </section>
    );
};
